import type { ConversionTrackingEventInput } from "@circle-react/api/conversionTrackingApi";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useConversionTrackingAttribution } from "@circle-react/hooks/useConversionTrackingAttribution";
import { useConversionTrackingConsent } from "@circle-react/hooks/useConversionTrackingConsent";
import { useConversionTrackingEventTrigger } from "@circle-react/hooks/useConversionTrackingEventTrigger";
import type { ConversionTrackingAttributionData } from "@circle-react/lib/queries/conversionTracking";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";

interface ConversionTrackingContextValue {
  attributionData: ConversionTrackingAttributionData;
  metaPixelId?: string;
  track: (event: ConversionTrackingEventInput) => boolean;
}

interface ConversionTrackingContextProviderProps {
  children: ReactNode;
}

const DEFAULT_CONVERSION_TRACKING_CONTEXT: ConversionTrackingContextValue = {
  attributionData: {
    landing_url: "",
    referrer_url: "",
  },
  track: () => false,
};

const ConversionTrackingContext = createContext<ConversionTrackingContextValue>(
  DEFAULT_CONVERSION_TRACKING_CONTEXT,
);
ConversionTrackingContext.displayName = "ConversionTrackingContext";

export const ConversionTrackingContextProvider = ({
  children,
}: ConversionTrackingContextProviderProps) => {
  const isConsentGranted = useConversionTrackingConsent();
  const { currentCommunitySettings, isLoading: isPunditUserLoading } =
    usePunditUserContext();
  const conversionTrackingSettings =
    currentCommunitySettings?.conversion_tracking_settings;
  const hasEnabledProvider = conversionTrackingSettings?.enabled ?? false;
  const isSettingsPending = isPunditUserLoading && !currentCommunitySettings;
  const metaPixelId = conversionTrackingSettings?.meta_pixel_id;
  const { attributionData, isCapturePending, isReady } =
    useConversionTrackingAttribution({
      hasEnabledProvider,
      isConsentGranted,
      metaPixelId,
    });
  const track = useConversionTrackingEventTrigger({
    hasEnabledProvider,
    isCapturePending,
    isConsentGranted,
    isSettingsPending,
    isReady,
    metaPixelId,
  });

  return (
    <ConversionTrackingContext.Provider
      value={{
        attributionData,
        metaPixelId,
        track,
      }}
    >
      {children}
    </ConversionTrackingContext.Provider>
  );
};

export const useConversionTrackingContext =
  (): ConversionTrackingContextValue => useContext(ConversionTrackingContext);
